<script setup lang="ts">
  import Box from '@leaflink/stash/Box.vue';
  import Button from '@leaflink/stash/Button.vue';
  import Dropdown from '@leaflink/stash/Dropdown.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Illustration from '@leaflink/stash/Illustration.vue';
  import Input from '@leaflink/stash/Input.vue';
  import Menu from '@leaflink/stash/Menu.vue';
  import MenuItem from '@leaflink/stash/MenuItem.vue';
  import PageContent from '@leaflink/stash/PageContent.vue';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';
  import { RouterLink } from 'vue-router';

  import { ROUTE_NAMES } from '@/constants';
  import notify from '@/notify';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';
  import copyStringToClipboard from '@/utils/copyStringToClipboard';

  const { user } = storeToRefs(useUserStore());

  const userReferralLink = computed(() => `${window.location.origin}/${user.value.referralCode}`);

  function copyShareText(item: 'code' | 'link') {
    copyStringToClipboard(item === 'code' ? user.value.referralCode : userReferralLink.value);

    notify.success(i18n.t('user.shareLeafLink.copiedToClipboard'));
  }
</script>

<template>
  <div>
    <PageContent class="tw-mt-6">
      <Box
        class="tw-flex tw-flex-col lg:tw-flex-row lg:tw-items-center lg:tw-justify-between tw-mb-[1px] tw-rounded-b-none"
      >
        <div class="tw-flex tw-items-center">
          <Illustration class="tw-mr-1" name="money" />
          <h3>{{ i18n.t('user.shareLeafLink.title') }}</h3>
        </div>

        <Dropdown data-test="dropdown|my-referral-reports">
          <template #toggle="{ toggle }">
            <Button
              secondary
              class="tw-flex tw-w-full tw-text-left tw-justify-between tw-items-center"
              color="blue"
              @click="toggle"
            >
              <span>{{ i18n.t('user.shareLeafLink.myReferralReports') }}</span>
              <Icon name="caret-down" />
            </Button>
          </template>

          <template #default>
            <Menu>
              <MenuItem v-for="company in user.companies" :key="company.slug">
                <RouterLink :to="{ name: ROUTE_NAMES.REFERRALS_REPORT, params: { company: company.slug } }">
                  {{ company.name }}
                </RouterLink>
              </MenuItem>
            </Menu>
          </template>
        </Dropdown>
      </Box>

      <Box class="tw-rounded-t-none">
        <div class="lg:tw-flex lg:tw-gap-6">
          <div class="tw-flex tw-flex-col lg:tw-w-[50%]">
            <h3 class="tw-mb-3">{{ i18n.t('user.shareLeafLink.howItWorks.header') }}</h3>

            <ol class="tw-list-decimal tw-ml-3">
              <li>{{ i18n.t('user.shareLeafLink.howItWorks.steps.one') }}</li>
              <li>{{ i18n.t('user.shareLeafLink.howItWorks.steps.two') }}</li>
              <li>{{ i18n.t('user.shareLeafLink.howItWorks.steps.three') }}</li>
              <li>{{ i18n.t('user.shareLeafLink.howItWorks.steps.four') }}</li>
            </ol>

            <small class="tw-mt-6">
              {{ i18n.t('user.shareLeafLink.howItWorks.disclaimer') }}
            </small>
          </div>

          <div class="tw-flex tw-flex-col max-lg:tw-mt-3 lg:tw-w-[50%]">
            <div class="tw-mb-6">
              <Input disabled :label="i18n.t('user.shareLeafLink.referralCode')" :model-value="user.referralCode">
                <template #append>
                  <Button data-test="button|copy-referral-code" secondary @click="copyShareText('code')">
                    <Icon class="tw-text-blue-500" name="copy" />
                  </Button>
                </template>
              </Input>
            </div>

            <div>
              <Input disabled :label="i18n.t('user.shareLeafLink.referralLink')" :model-value="userReferralLink">
                <template #append>
                  <Button data-test="button|copy-referral-link" secondary @click="copyShareText('link')">
                    <Icon class="tw-text-blue-500" name="copy" />
                  </Button>
                </template>
              </Input>
            </div>
          </div>
        </div>
      </Box>
    </PageContent>
  </div>
</template>
